/**
 * Author: Romain Hapi Sr.
 * Version: 1.0
 * Date: 2026-06-25
 * Project: Prenim Digital
 * Copyright: Prenim UK
 */
import React from 'react';
import { Users, Globe, Award, Target, Heart } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { PageHero, SectionHead } from '../components/Section';
import { useReveal } from '../hooks/useReveal';
import SEO from '../../components/SEO';
import { SHELL, SECTION, H3, LEAD, CARD } from '../theme';

const META = {
  fr: {
    title: 'A propos — Prenim Tchad',
    description:
      "Prenim Tchad : nee a Leeds en 2021, installee a N'Djamena depuis decembre 2025. Notre histoire, notre mission et nos valeurs.",
  },
  en: {
    title: 'About — Prenim Tchad',
    description:
      "Prenim Tchad: founded in Leeds in 2021, established in N'Djamena since December 2025. Our story, mission and values.",
  },
  ar: {
    title: 'من نحن — Prenim Tchad',
    description: 'تأسست في ليدز عام 2021 واستقرت في نجامينا منذ ديسمبر 2025.',
  },
};

const EYEBROW = { fr: 'Qui sommes-nous', en: 'Who we are', ar: 'من نحن' };

/**
 * Le texte de l'histoire porte du gras au format Markdown (`**Prenim Tchad**`).
 * Rendu tel quel, il affichait les asterisques a l'ecran. On decoupe sur les
 * paires d'asterisques : les positions impaires sont les passages en gras.
 */
const RichText = ({ value }) =>
  value.split(/\n\s*\n/).map((paragraph, index) => (
    <p key={index} className={index ? `mt-6 ${LEAD}` : LEAD}>
      {paragraph.split('**').map((chunk, i) =>
        i % 2 ? (
          <strong key={i} className="font-semibold text-slate-950">
            {chunk}
          </strong>
        ) : (
          <React.Fragment key={i}>{chunk}</React.Fragment>
        )
      )}
    </p>
  ));

export const About = () => {
  const { language } = useLanguage();

  const content = {
    fr: {
      hero: {
        title: 'À Propos de Prenim Tchad',
        subtitle: 'Acteur majeur de la transformation digitale en Afrique et en Europe'
      },
      story: {
        title: 'Notre Histoire',
        content: `Fondée en août 2021 au Royaume-Uni à Leeds sous le nom de Prenim, l'entreprise est née de la vision de créer un pont technologique entre l'Europe et l'Afrique. En décembre 2025, nous avons ouvert notre bureau à N'Djamena et établi le Tchad comme siège social principal sous le nom de **Prenim Tchad**, marquant notre engagement profond envers le continent africain.

Spécialisés dans la transformation digitale, le conseil IT, l'intégration de systèmes, le développement d'applications web et desktop, le DevOps et les réseaux, nous apportons une expertise de classe mondiale aux entreprises africaines et européennes depuis notre siège tchadien, avec un bureau de soutien à Leeds.

Aujourd'hui, avec plus de 50 projets réalisés dans 15 pays africains, Prenim Tchad continue à repousser les limites de l'innovation technologique et à transformer les entreprises grâce au numérique.`
      },
      mission: {
        title: 'Notre Mission',
        content: 'Accompagner les entreprises africaines et européennes dans leur transformation digitale en fournissant des solutions technologiques innovantes, fiables et adaptées à leurs besoins spécifiques.'
      },
      vision: {
        title: 'Notre Vision',
        content: 'Être le partenaire technologique de référence pour les entreprises qui souhaitent moderniser leurs opérations et accélérer leur croissance grâce au digital.'
      },
      values: {
        title: 'Nos Valeurs',
        items: [
          { icon: Target, title: 'Excellence', description: 'Nous visons l\'excellence dans chaque projet et chaque interaction' },
          { icon: Heart, title: 'Passion', description: 'Notre passion pour la technologie nous pousse à innover constamment' },
          { icon: Users, title: 'Collaboration', description: 'Nous travaillons en étroite collaboration avec nos clients' },
          { icon: Award, title: 'Intégrité', description: 'Nous agissons avec honnêteté et transparence' }
        ]
      },
      stats: [
        { value: '50+', label: 'Projets Réalisés' },
        { value: '15+', label: 'Pays Africains' },
        { value: '2', label: 'Bureaux' },
        { value: '100%', label: 'Satisfaction Client' }
      ]
    },
    en: {
      hero: {
        title: 'About Prenim Tchad',
        subtitle: 'Major consulting firm of digital transformation in Africa and Europe'
      },
      story: {
        title: 'Our Story',
        content: `Founded in August 2021 in the United Kingdom in Leeds under the name Prenim, the company was born from the vision of creating a technological bridge between Europe and Africa. In December 2025 we opened our N'Djamena office and established Chad as the main headquarters under the name **Prenim Tchad**, reflecting our deep commitment to the African continent.

Specialized in digital transformation, IT consulting, systems integration, web and desktop application development, DevOps and networking, we bring world-class expertise to African and European businesses from our Chadian headquarters, with a support office in Leeds.

Today, with over 50 projects completed in 15 African countries, Prenim Tchad continues to push the boundaries of technological innovation and transform businesses through digital.`
      },
      mission: {
        title: 'Our Mission',
        content: 'Support African and European companies in their digital transformation by providing innovative, reliable and tailored technological solutions to their specific needs.'
      },
      vision: {
        title: 'Our Vision',
        content: 'To be the reference technology partner for companies looking to modernize their operations and accelerate their growth through digital.'
      },
      values: {
        title: 'Our Values',
        items: [
          { icon: Target, title: 'Excellence', description: 'We aim for excellence in every project and every interaction' },
          { icon: Heart, title: 'Passion', description: 'Our passion for technology drives us to constantly innovate' },
          { icon: Users, title: 'Collaboration', description: 'We work closely with our clients' },
          { icon: Award, title: 'Integrity', description: 'We act with honesty and transparency' }
        ]
      },
      stats: [
        { value: '50+', label: 'Projects Completed' },
        { value: '15+', label: 'African Countries' },
        { value: '2', label: 'Offices' },
        { value: '100%', label: 'Client Satisfaction' }
      ]
    },
    ar: {
      hero: {
        title: 'عن Prenim Tchad',
        subtitle: 'شركة استشارية رائدة في مجال التحول الرقمي في أفريقيا وأوروبا'
      },
      story: {
        title: 'قصتنا',
        content: `تأسست الشركة في أغسطس 2021 في المملكة المتحدة في ليدز تحت اسم Prenim، ولدت من رؤية إنشاء جسر تكنولوجي بين أوروبا وأفريقيا. في ديسمبر 2025، افتتحنا مكتبنا في نجامينا واتخذنا تشاد مقراً رئيسياً تحت اسم **Prenim Tchad**، مما يعكس التزامنا العميق بالقارة الأفريقية.

متخصصون في التحول الرقمي والاستشارات التقنية وتكامل الأنظمة وتطوير تطبيقات الويب وسطح المكتب وDevOps والشبكات، نقدم خبرة عالمية للشركات الأفريقية والأوروبية من مقرنا التشادي، مع مكتب دعم في ليدز.

اليوم، مع أكثر من 50 مشروعًا منجزًا في 15 دولة أفريقية، تواصل Prenim Tchad تجاوز حدود الابتكار التكنولوجي وتحويل الشركات من خلال الرقمنة.`
      },
      mission: {
        title: 'مهمتنا',
        content: 'دعم الشركات الأفريقية والأوروبية في تحولها الرقمي من خلال توفير حلول تكنولوجية مبتكرة وموثوقة ومخصصة لاحتياجاتهم المحددة.'
      },
      vision: {
        title: 'رؤيتنا',
        content: 'أن نكون الشريك التكنولوجي المرجعي للشركات التي تتطلع إلى تحديث عملياتها وتسريع نموها من خلال الرقمنة.'
      },
      values: {
        title: 'قيمنا',
        items: [
          { icon: Target, title: 'التميز', description: 'نهدف إلى التميز في كل مشروع وكل تفاعل' },
          { icon: Heart, title: 'الشغف', description: 'شغفنا بالتكنولوجيا يدفعنا للابتكار باستمرار' },
          { icon: Users, title: 'التعاون', description: 'نعمل عن كثب مع عملائنا' },
          { icon: Award, title: 'النزاهة', description: 'نتصرف بأمانة وشفافية' }
        ]
      },
      stats: [
        { value: '50+', label: 'مشروع منجز' },
        { value: '15+', label: 'دولة أفريقية' },
        { value: '2', label: 'مكتب' },
        { value: '100%', label: 'رضا العملاء' }
      ]
    }
  };

  const t = content[language] || content.fr;

  const meta = META[language] || META.fr;
  const stats = useReveal();
  const purpose = useReveal();
  const values = useReveal();

  return (
    <main data-testid="tchad-about-page">
      <SEO title={meta.title} description={meta.description} lang={language} />

      <PageHero
        eyebrow={EYEBROW[language] || EYEBROW.fr}
        title={t.hero.title}
        lead={t.hero.subtitle}
        image="/images/hero-tchad.webp"
      />

      {/* Chiffres : filets fins et alignement a gauche, sans degrade sur le
          texte ni agrandissement au survol. */}
      <section className={`${SECTION} bg-white`}>
        <div className={SHELL}>
          <div
            ref={stats.ref}
            className={`${stats.className} grid gap-px border-y border-slate-200 bg-slate-200 sm:grid-cols-2 lg:grid-cols-4`}
          >
            {t.stats.map((stat, index) => (
              <div
                key={index}
                data-testid={`tchad-about-stat-${index}`}
                className="bg-white px-2 py-8"
              >
                <div className="text-4xl font-bold tracking-tight text-slate-950 lg:text-5xl">
                  {stat.value}
                </div>
                <div className="no-justify mt-2 text-xs uppercase tracking-wider text-slate-500">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Histoire : grille asymetrique 4/8, texte sur une colonne de lecture
          confortable plutot que centre sur toute la largeur. */}
      <section className={`${SECTION} bg-slate-50`}>
        <div className={SHELL}>
          <div className="grid gap-12 lg:grid-cols-12">
            <div className="lg:col-span-4">
              <SectionHead title={t.story.title} />
            </div>
            <div className="lg:col-span-8">
              <RichText value={t.story.content} />
            </div>
          </div>
        </div>
      </section>

      <section className={`${SECTION} bg-white`}>
        <div className={SHELL}>
          <div ref={purpose.ref} className={`${purpose.className} grid gap-8 md:grid-cols-2`}>
            {[
              { icon: Target, title: t.mission.title, content: t.mission.content },
              { icon: Globe, title: t.vision.title, content: t.vision.content },
            ].map(({ icon: Icon, title, content: text }) => (
              <div key={title} className={`${CARD} p-10`}>
                <Icon className="h-7 w-7 text-amber-600" strokeWidth={1.5} />
                <h2 className="mt-6 text-2xl font-bold tracking-tight text-slate-950 lg:text-3xl">
                  {title}
                </h2>
                <p className={`mt-4 ${LEAD}`}>{text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className={`${SECTION} bg-slate-50`}>
        <div className={SHELL}>
          <SectionHead title={t.values.title} />
          <div
            ref={values.ref}
            className={`${values.className} mt-16 grid gap-8 sm:grid-cols-2 lg:grid-cols-4`}
          >
            {t.values.items.map((value, index) => {
              const Icon = value.icon;
              return (
                <div
                  key={index}
                  data-testid={`tchad-value-${index}`}
                  className={`${CARD} p-8`}
                >
                  <Icon className="h-6 w-6 text-amber-600" strokeWidth={1.5} />
                  <h3 className={`${H3} mt-5 text-slate-950`}>{value.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-slate-600">
                    {value.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>
    </main>
  );
};
